import _ from 'lodash'
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import specialityOptionModel, {
  CreateSpecialityOptionDto,
  SpecialityOption,
  SpecialityOptionState,
  UpdateSpecialityOptionDto,
} from '@/core/models/speciality-option.model'

import { RootState } from '@/redux/rootReducer'
import { SearchRequest } from '@/core/types/request'

export type SpecialityOptionReducerState = SpecialityOptionState

export const specialityOptionAdapter = createEntityAdapter<SpecialityOption, SpecialityOption['_id']>({ selectId: (specialityOption) => specialityOption._id })

const specialityOptionSelector = specialityOptionAdapter.getSelectors((state: RootState) => state.specialityOption.specialityOptions)

const initialState: SpecialityOptionReducerState = _.merge({}, specialityOptionModel.getInitialState())

export const latestCreatedSpecialityOptionIdSelector = (state: RootState) => state.specialityOption.newlyCreatedItemId

const specialityOptionSlice = createSlice({
  name: specialityOptionModel.reducerName,
  initialState,
  reducers: {
    createSpecialityOption(state, action: PayloadAction<CreateSpecialityOptionDto>) {
      state.requestTracker.create.loading = true
      state.newlyCreatedItemId = undefined
    },
    createSpecialityOptionSuccess(state, action: PayloadAction<SpecialityOption>) {
      specialityOptionAdapter.setOne(state.specialityOptions, action.payload)
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
      state.newlyCreatedItemId = action.payload._id
    },
    createSpecialityOptionFailure(state, action) {
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    deleteSpecialityOption(state, action: PayloadAction<string>) {
      state.requestTracker.delete.loading = true
    },
    deleteSpecialityOptionSuccess(state, action: PayloadAction<string>) {
      specialityOptionAdapter.removeOne(state.specialityOptions, action.payload)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deleteSpecialityOptionFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    getSpecialityOptions(state, action: PayloadAction<SearchRequest | undefined>) {
      state.requestTracker.list.loading = true
    },
    getSpecialityOptionsSuccess(state, action: PayloadAction<SpecialityOption[]>) {
      specialityOptionAdapter.setAll(state.specialityOptions, action.payload)
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getSpecialityOptionsFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    updateSpecialityOption: {
      reducer: (state, action: PayloadAction<{ specialityOptionId: string; updateSpecialityOptionDto: UpdateSpecialityOptionDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (specialityOptionId: string, updateSpecialityOptionDto: UpdateSpecialityOptionDto) => ({
        payload: { specialityOptionId, updateSpecialityOptionDto },
      }),
    },
    updateSpecialityOptionSuccess(state, action: PayloadAction<SpecialityOption>) {
      specialityOptionAdapter.setOne(state.specialityOptions, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateSpecialityOptionFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
  },
})

export const selectSpecialityOptions = specialityOptionSelector.selectAll

export const selectSpecialityOptionCreateRequestTracker = (state: RootState) => state.specialityOption.requestTracker.create
export const selectSpecialityOptionDeleteRequestTracker = (state: RootState) => state.specialityOption.requestTracker.delete
export const selectSpecialityOptionListRequestTracker = (state: RootState) => state.specialityOption.requestTracker.list
export const selectSpecialityOptionUpdateRequestTracker = (state: RootState) => state.specialityOption.requestTracker.update

export const {
  createSpecialityOption,
  createSpecialityOptionSuccess,
  createSpecialityOptionFailure,
  deleteSpecialityOption,
  deleteSpecialityOptionSuccess,
  deleteSpecialityOptionFailure,
  getSpecialityOptions,
  getSpecialityOptionsSuccess,
  getSpecialityOptionsFailure,
  updateSpecialityOption,
  updateSpecialityOptionSuccess,
  updateSpecialityOptionFailure,
} = specialityOptionSlice.actions

export default specialityOptionSlice.reducer
